import React, { useState, useEffect } from "react";
import { usePaginated } from "../hooks";
import { api } from "../api";
import { fmtMoney, SC } from "../utils";
import { useToast, useConfirm, Btn, Card, Spinner, ApiError, Empty, Badge, Pages } from "../ui";

import { Ico } from "./primitives";
import { G, GL, TRIP_TYPES, PROGRESS_STATUS, statusDot, effectivePaid } from "./constants";
import { BRAND_GRADIENT } from "../theme";
import ClientFormModal from "./ClientFormModal";
import ClientDetailModal from "./ClientDetailModal";
import QuickTaskModal from "./QuickTaskModal";
import { createDraftRefund } from "./refundDraft";

// ─── CLIENTS TAB ─────────────────────────────────────────────────────────────
export function ClientsTab() {
  const toast = useToast();
  const confirm = useConfirm();

  const [tripTypeFilter, setTripTypeFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState("");

  const filterParams = new URLSearchParams();
  if (tripTypeFilter) filterParams.set("tripType", tripTypeFilter);
  if (progressFilter) filterParams.set("progressStatus", progressFilter);
  const basePath = `/crm/clients${filterParams.toString() ? "?" + filterParams.toString() : ""}`;

  // ── Search ──────────────────────────────────────────────────────────────
  // The list is paginated (50/page) and search is a local .filter(), so a match
  // living on another page was invisible until you manually paged to it. Fix: when
  // a search term is active we pull the whole (filtered) list in ONE request and
  // filter locally, so matches on ANY page show immediately. Empty search → normal
  // 50-per-page pagination.
  const [searchInput, setSearchInput] = useState(""); // raw text being typed
  const [search, setSearch] = useState("");           // debounced value we filter on

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const isSearching = search.length > 0;

  const { data, total, page, totalPages, loading, error, setPage, refetch } =
    usePaginated(basePath, isSearching ? 1000 : 50);

  useEffect(() => { setPage(1); }, [tripTypeFilter, progressFilter]);
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);

  const shown = (data || []).filter(
    (c) =>
      !search ||
      c.fullName.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase())
  );

  const openDetail = async (c) => {
    try {
      const full = await api.get(`/crm/clients/${c._id}`);
      setSel(full);
      setModal("detail");
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // CREATE: full wizard payload (personal + optional first trip)
  const doCreate = async (payload, refundDraft) => {
    setSaving(true);
    try {
      const created = await api.post("/crm/clients", payload);
      if (refundDraft) {
        try {
          await createDraftRefund(created, refundDraft);
        } catch (e) {
          toast(`Client created, but the refund failed: ${e.message}`, "warn");
        }
      }
      toast(refundDraft ? "Client and refund created ✓" : "Client created ✓");
      setModal(null);
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // EDIT: personal fields only — never sends `trips`, so existing trips are preserved.
  // Trips are added/edited/removed from inside ClientDetailModal via the dedicated
  // /trips sub-resource endpoints.
  const doEdit = async (payload) => {
    setSaving(true);
    try {
      await api.patch(`/crm/clients/${sel._id}`, payload);
      toast("Saved ✓");
      setModal(null);
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    if (!(await confirm({ title: "Delete client?", message: "This permanently removes the client and all of their trips. This cannot be undone.", confirmText: "Delete client" }))) return;
    try {
      await api.delete(`/crm/clients/${id}`);
      toast("Client deleted");
      refetch();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight">Clients</h1>
            <p className="text-sm text-slate-400 mt-0.5">{total} clients in your database</p>
          </div>
          <Btn variant="primary" onClick={() => { setSel(null); setModal("create"); }}>
            <Ico d="M12 4v16m8-8H4" sz={14} col="white" /> New Client
          </Btn>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Ico
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              sz={15}
              col="#94a3b8"
              cls="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
            />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:border-[rgb(var(--brand-rgb))] bg-white"
            />
          </div>

          <select
            value={tripTypeFilter}
            onChange={(e) => setTripTypeFilter(e.target.value)}
            className="px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 outline-none focus:ring-2 focus:border-[rgb(var(--brand-rgb))] bg-white min-w-[160px]"
          >
            <option value="">All Trip Types</option>
            {TRIP_TYPES.map((t) => (
              <option key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>
            ))}
          </select>

          <select
            value={progressFilter}
            onChange={(e) => setProgressFilter(e.target.value)}
            className="px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 outline-none focus:ring-2 focus:border-[rgb(var(--brand-rgb))] bg-white min-w-[180px]"
          >
            <option value="">All Progress Statuses</option>
            {PROGRESS_STATUS.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>
            ))}
          </select>

          {(tripTypeFilter || progressFilter) && (
            <button
              onClick={() => { setTripTypeFilter(""); setProgressFilter(""); }}
              className="px-3 py-2.5 rounded-xl border border-rose-200 text-xs font-bold text-rose-500 hover:bg-rose-50 transition-colors whitespace-nowrap"
            >
              ✕ Clear
            </button>
          )}
        </div>

        {(tripTypeFilter || progressFilter) && (
          <div className="flex gap-2 flex-wrap">
            {tripTypeFilter && (
              <span className="inline-flex items-center gap-1.5 bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-dark-rgb))] text-xs font-bold px-3 py-1 rounded-full">
                Trip: {tripTypeFilter}
              </span>
            )}
            {progressFilter && (
              <span className="inline-flex items-center gap-1.5 bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-dark-rgb))] text-xs font-bold px-3 py-1 rounded-full">
                Status: {progressFilter.replace(/_/g, " ")}
              </span>
            )}
          </div>
        )}

        {error && <ApiError msg={error} onRetry={refetch} />}
        {loading && <Spinner full />}

        {!loading && !error && (
          <>
            {!shown.length ? (
              <Empty
                icon="clients"
                title="No clients found"
                sub={
                  search || tripTypeFilter || progressFilter
                    ? "Try adjusting your filters"
                    : "Add your first client to get started"
                }
                action={
                  !search && !tripTypeFilter && !progressFilter && (
                    <Btn variant="primary" onClick={() => setModal("create")}>New Client</Btn>
                  )
                }
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {shown.map((c) => {
                  // Card preview shows the NEWEST trip as a summary; the full
                  // multi-trip view (switch / add / edit / delete) lives in the detail modal.
                  const trip = c.trips?.[c.trips.length - 1]; // newest trip
                  const tripCount = c.trips?.length || 0;
                  const pay = trip?.payment;
                  const cur = pay?.currency || "USD";
                  // Includes foreign-currency payments via their base-currency equivalents.
                  const paid = effectivePaid(pay);
                  const pct = pay?.totalTripCost
                    ? Math.min(100, (paid / pay.totalTripCost) * 100)
                    : null;
                  return (
                    <Card
                      key={c._id}
                      className="p-5 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                      onClick={() => openDetail(c)}
                    >
                      <div className="flex items-start justify-between mb-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-11 h-11 rounded-xl text-white font-black text-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: BRAND_GRADIENT }}
                          >
                            {c.fullName?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800 text-sm leading-tight group-hover:text-[rgb(var(--brand-rgb))] transition-colors">
                              {c.fullName}
                            </p>
                            <p className="text-xs text-slate-400 truncate max-w-[140px]">{c.email}</p>
                          </div>
                        </div>
                        {c.returningClient && (
                          <span
                            className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0"
                            style={{ background: GL, color: G }}
                          >
                            ↩
                          </span>
                        )}
                      </div>

                      <div className="flex items-center text-xs text-slate-400 mb-3 gap-1.5">
                        <span className="truncate">{c.nationality || "–"}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200 flex-shrink-0" />
                        <span className="truncate">{c.countryOfResidence || "–"}</span>
                        <span className="ml-auto font-medium text-slate-500 flex-shrink-0">
                          {tripCount} trip{tripCount !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {trip && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot(trip.progressStatus)}`} />
                          <span className="text-xs text-slate-500 truncate">{trip.destinations?.join(", ")}</span>
                          <Badge label={trip.progressStatus} colorClass={SC[trip.progressStatus] || "bg-slate-100 text-slate-500"} />
                          {tripCount > 1 && (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: GL, color: G }}>
                              +{tripCount - 1}
                            </span>
                          )}
                        </div>
                      )}

                      {trip?.tripType && (
                        <div className="mb-2">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: GL, color: G }}>
                            {trip.tripType.replace(/_/g, " ")}
                          </span>
                        </div>
                      )}

                      {pct !== null && (
                        <div className="mb-3.5">
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${pct}%`, background: pct >= 100 ? "#10b981" : pct >= 50 ? G : "#f43f5e" }}
                            />
                          </div>
                          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                            <span>{fmtMoney(paid, cur)}</span>
                            <span>{Math.round(pct)}% · {cur}</span>
                            <span>{fmtMoney(pay.totalTripCost, cur)}</span>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                        <Btn variant="secondary" size="sm" className="flex-1" onClick={() => openDetail(c)}>
                          <Ico d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" sz={12} /> View
                        </Btn>
                        <Btn variant="ghost" size="sm" onClick={() => { setSel(c); setModal("edit"); }}>
                          <Ico d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" sz={12} />
                        </Btn>
                        <Btn variant="danger" size="sm" onClick={() => doDelete(c._id)}>
                          <Ico d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" sz={12} />
                        </Btn>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
            {isSearching ? (
              <p className="text-xs text-slate-400 text-right mt-4">
                {shown.length} result{shown.length !== 1 ? "s" : ""} for &quot;{search}&quot;
              </p>
            ) : (
              <Pages page={page} totalPages={totalPages} total={total} onPage={setPage} loading={loading} />
            )}
          </>
        )}
      </div>

      {modal === "create" && (
        <ClientFormModal
          onSubmit={doCreate}
          onClose={() => setModal(null)}
          saving={saving}
          onOpenExisting={(c) => { setModal(null); openDetail(c); }}
        />
      )}
      {modal === "edit" && sel && (
        <ClientFormModal initial={sel} onSubmit={doEdit} onClose={() => setModal(null)} saving={saving} />
      )}
      {modal === "detail" && sel && (
        <ClientDetailModal
          client={sel}
          onClose={() => setModal(null)}
          onEdit={() => setModal("edit")}
          onAddTask={() => setModal("task")}
          onClientChanged={refetch}
        />
      )}
      {modal === "task" && sel && (
        <QuickTaskModal
          clientId={sel._id}
          clientName={sel.fullName}
          onDone={() => {
            setModal("detail");
            api.get(`/crm/clients/${sel._id}`).then(setSel).catch(() => {});
          }}
          onClose={() => setModal("detail")}
        />
      )}
    </>
  );
}

// ── Re-export so existing consumers keep working ──
// TripsTab.jsx imports this as a NAMED import:
//   import { ClientDetailModal } from "../clients/ClientsTab";
// ClientDetailModal is a default export in its own file, surfaced as a named export here.
export { default as ClientDetailModal } from "./ClientDetailModal";